import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { 
  VitalSignsDataModel, 
  VitalSignsDto, 
  SaveVitalSignRequest, 
  DeleteVitalSignRequest,
  VitalSignsDateRangeQuery 
} from '../models/vital-signs/vital-signs-data.model';

@Injectable({
  providedIn: 'root'
})
export class VitalSignsService {

  constructor(private readonly http: HttpClient) { }

  // 📋 GET Methods (Data Retrieval)

  /**
   * 1. Get All Patient Vital Signs
   * Purpose: Retrieve all vital signs for a specific patient
   * @param patientId - The patient ID to get vital signs for
   * @returns Observable of vital signs data array ordered by date descending
   */
  getPatientVitalSigns(patientId: string): Observable<VitalSignsDto[]> {
    return this.http.get<VitalSignsDto[]>(`api/vitalsigns/get-patient-vitalsigns/${patientId}`)
      .pipe(
        catchError(this.handleError<VitalSignsDto[]>('getPatientVitalSigns', []))
      );
  }

  /**
   * 2. Get Anesthesia-Specific Vital Signs
   * Purpose: Retrieve vital signs linked to a specific anesthesia session
   * @param anesthesiaId - The anesthesia ID to get vital signs for
   * @returns Observable of vital signs data array for that anesthesia session
   */
  getAnesthesiaVitalSigns(anesthesiaId: string): Observable<VitalSignsDto[]> {
    return this.http.get<VitalSignsDto[]>(`api/vitalsigns/get-anesthesia-vitalsigns/${anesthesiaId}`)
      .pipe(
        catchError(this.handleError<VitalSignsDto[]>('getAnesthesiaVitalSigns', []))
      );
  }

  /**
   * 3. Get Single Vital Sign Record
   * Purpose: Retrieve one specific vital sign record
   * @param vitalSignId - The vital sign ID to retrieve
   * @returns Observable of single vital sign record or null
   */
  getSingleVitalSign(vitalSignId: string): Observable<VitalSignsDto | null> {
    return this.http.get<VitalSignsDto>(`api/vitalsigns/get-vitalsign/${vitalSignId}`)
      .pipe(
        catchError(this.handleError<VitalSignsDto | null>('getSingleVitalSign', null))
      );
  }

  /**
   * 4. Get Latest Vital Sign
   * Purpose: Get the most recent vital sign for a patient
   * @param patientId - The patient ID to get latest vital sign for
   * @returns Observable of latest vital sign or null if none exist
   */
  getLatestVitalSign(patientId: string): Observable<VitalSignsDto | null> {
    return this.http.get<VitalSignsDto>(`api/vitalsigns/get-latest-vitalsign/${patientId}`)
      .pipe(
        catchError(this.handleError<VitalSignsDto | null>('getLatestVitalSign', null))
      );
  }

  /**
   * 5. Get Vital Signs by Date Range
   * Purpose: Retrieve vital signs within a specific date range
   * @param query - Query parameters including patientId, startDate, endDate
   * @returns Observable of vital signs filtered by date range
   */
  getVitalSignsByDateRange(query: VitalSignsDateRangeQuery): Observable<VitalSignsDto[]> {
    let params = new HttpParams().set('PatientId', query.PatientId);
    
    if (query.startDate) {
      params = params.set('startDate', query.startDate.toISOString());
    }
    if (query.endDate) {
      params = params.set('endDate', query.endDate.toISOString());
    }

    return this.http.get<VitalSignsDto[]>('api/vitalsigns/get-vitalsigns-daterange', { params })
      .pipe(
        catchError(this.handleError<VitalSignsDto[]>('getVitalSignsByDateRange', []))
      );
  }

  // ✏️ POST/PUT Methods (Data Modification)

  /**
   * 6. Save Vital Sign (Create/Update)
   * Purpose: Create new or update existing vital sign record
   * @param request - Save vital sign request with all vital sign data
   * @returns Observable of saved vital sign record
   */
  saveVitalSign(request: SaveVitalSignRequest): Observable<VitalSignsDto> {
    return this.http.post<VitalSignsDto>('api/vitalsigns/save-vitalsign', request)
      .pipe(
        catchError(this.handleError<VitalSignsDto>('saveVitalSign'))
      );
  }


  // 🗑️ DELETE Methods (Data Removal)

  /**
   * 7. Delete Vital Sign (Legacy POST)
   * Purpose: Soft delete vital sign record (legacy endpoint)
   * @param request - Delete vital sign request
   * @returns Observable of delete result
   */
  deleteVitalSignLegacy(request: DeleteVitalSignRequest): Observable<any> {
    return this.http.post<any>('api/vitalsigns/delete-vitalsign-legacy', request)
      .pipe(
        catchError(this.handleError<any>('deleteVitalSignLegacy'))
      );
  }

  /**
   * 8. Delete Vital Sign by ID (Preferred)
   * Purpose: Soft delete vital sign record by ID
   * @param vitalSignId - The vital sign ID to delete
   * @returns Observable of delete result
   */
  deleteVitalSignById(vitalSignId: string): Observable<any> {
    return this.http.delete<any>(`api/vitalsigns/delete-vitalsign/${vitalSignId}`)
      .pipe(
        catchError(this.handleError<any>('deleteVitalSignById'))
      );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      
      // Let the app keep running by returning an empty result.
      return new Observable<T>(observer => {
        observer.next(result as T);
        observer.complete();
      });
    };
  }
}
